import { store } from '../store'
import DynamicModuleRegistrationUtil from '../utils/dynamicModuleRegistration'

// Vuex Modules to load
const WavepickingStoreModule = () => import(/* webpackChunkName: "ManageWavePicking" */ '../store/modules/wavePicking')
const WavepickingAppStoreModule = () => import(/* webpackChunkName: "WavePickingApp" */ '../store/modules/wavePickingApp')

// Components to load
const ManageWavePicking = () => import(/* webpackChunkName: "ManageWavePicking" */ '@/components/wave-picking/manageWavePicking')
const WavePickingApp = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp')
const WavePickingAppStart = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/Start')
const WavePickingAppScanCart = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/ScanCart')
const WavePickingAppPick = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/Pick')
const WavePickingAppPlace = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/Place')
const WavePickingAppProgress = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/Progress')
const WavePickingAppConfirmBinStatus = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/ConfirmBinStatus')
const WavePickingAppDone = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/Done')
const WavePickingAppExceptions = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/Exceptions')
const WavePickingAppException = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/Exception')
const WavePickingAppCartView = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/CartView')
const WavePickingAppOrderSummary = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/OrderSummary')
const WavePickingAppPickException = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/PickException')
const WavePickingAppPlaceException = () => import(/* webpackChunkName: "WavePickingApp" */ '@/components/wave-picking/WavePickingApp/PlaceException')

// ******************************************************
// *************** Route Guard Functions ****************
// ******************************************************

const hasStartedCheck = (to, from, next) => {
  store.state.wavepickingApp.subwavePicklist === null ? next({ name: 'WavePickingStart' }) : next()
}

const subwaveCompleteCheck = async (to, from, next) => {
  const subwavePicklist = store.state.wavepickingApp.subwavePicklist
  if (subwavePicklist === null) {
    next({ name: 'WavePickingStart' })
  } else if (subwavePicklist.isComplete()) {
    await store.dispatch('wavepickingApp/fetchSubwaveExceptions', subwavePicklist.id)
    const subwaveExceptions = store.state.wavepickingApp.subwaveExceptions
    if (subwaveExceptions !== null && subwaveExceptions.numberOfExceptions() > 0) next({ name: 'WavePickingExceptions' })
    else next({ name: 'WavePickingDone' })

    // next({ name: 'WavePickingDone' })
  } else {
    next()
  }
}

const exceptionsCompleteCheck = (to, from, next) => {
  const subwavePicklist = store.state.wavepickingApp.subwavePicklist
  const subwaveExceptions = store.state.wavepickingApp.subwaveExceptions
  if (subwavePicklist === null) {
    next({ name: 'WavePickingStart' })
  } else if (subwaveExceptions.isComplete()) {
    next({ name: 'WavePickingDone' })
  } else next()
}

const dynamicModuleRegistrationWavePickingArgs = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'wavepicking',
    module: WavepickingStoreModule,
    store,
    modulePath: 'wavepicking'
  }
}

const dynamicModuleRegistrationWavePickingAppArgs = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'wavepickingApp',
    module: WavepickingAppStoreModule,
    store,
    modulePath: 'wavepickingApp'
  }
}

// ******************************************************
// ****************** Route Definitions *****************
// ******************************************************

export default [
  {
    path: '/ManageWavePicking',
    name: 'ManageWavePicking',
    component: ManageWavePicking,
    beforeEnter (to, from, next) {
      DynamicModuleRegistrationUtil(dynamicModuleRegistrationWavePickingArgs()).then(() => {
        next()
      })
    }
  },
  {
    path: '/WavePickingApp',
    name: 'WavePickingApp',
    component: WavePickingApp,
    beforeEnter (to, from, next) {
      DynamicModuleRegistrationUtil(dynamicModuleRegistrationWavePickingAppArgs()).then(() => {
        next()
      })
    },
    children: [
      {
        path: 'Progress',
        name: 'WavePickingProgress',
        meta: { title: 'Picking Progress' },
        component: WavePickingAppProgress,
        beforeEnter (to, from, next) {
          hasStartedCheck(to, from, next)
        }
      },
      {
        path: 'Start',
        name: 'WavePickingStart',
        meta: { title: 'Wave Picking' },
        component: WavePickingAppStart
      },
      {
        path: 'ScanCart',
        name: 'WavePickingScanCart',
        meta: { title: 'Scan Cart' },
        component: WavePickingAppScanCart,
        beforeEnter (to, from, next) {
          hasStartedCheck(to, from, next)
        }
      },
      {
        path: 'Pick',
        name: 'WavePickingPick',
        meta: { title: 'Pick Item' },
        component: WavePickingAppPick,
        async beforeEnter (to, from, next) {
          subwaveCompleteCheck(to, from, next)
        }
      },
      {
        path: 'Place',
        name: 'WavePickingPlace',
        meta: { title: 'Place Item' },
        component: WavePickingAppPlace,
        async beforeEnter (to, from, next) {
          subwaveCompleteCheck(to, from, next)
        }
      },
      {
        path: 'ConfirmBinStatus',
        name: 'WavePickingConfirmBinStatus',
        meta: { title: 'Confirm Bin Status' },
        component: WavePickingAppConfirmBinStatus,
        props: route => ({ item: route.params.confirmedItem }),
        beforeEnter (to, from, next) {
          hasStartedCheck(to, from, next)
        }
      },
      {
        path: 'Exception',
        name: 'WavePickingException',
        meta: { title: 'Exception Order' },
        component: WavePickingAppException,
        props: route => ({ item: route.params.order }),
        beforeEnter (to, from, next) {
          hasStartedCheck(to, from, next)
        }
      },
      {
        path: 'Done',
        name: 'WavePickingDone',
        meta: { title: 'Completed Picking' },
        component: WavePickingAppDone,
        beforeEnter (to, from, next) {
          hasStartedCheck(to, from, next)
        }
      },
      {
        path: 'Exceptions',
        name: 'WavePickingExceptions',
        meta: { title: 'Order Exceptions' },
        component: WavePickingAppExceptions,
        beforeEnter (to, from, next) {
          hasStartedCheck(to, from, next)
        }
      },
      {
        path: 'CartView',
        name: 'WavePickingCartView',
        meta: { title: 'Cart View' },
        component: WavePickingAppCartView,
        beforeEnter (to, from, next) {
          hasStartedCheck(to, from, next)
        }
      },
      {
        path: 'OrderSummary',
        name: 'WavePickingOrderSummary',
        meta: { title: 'Order Summary' },
        component: WavePickingAppOrderSummary,
        props: route => ({ bin: route.params.bin }),
        beforeEnter (to, from, next) {
          hasStartedCheck(to, from, next)
        }
      },
      {
        path: 'PickException',
        name: 'WavePickingPickException',
        meta: { title: 'Pick Exception Item' },
        component: WavePickingAppPickException,
        async beforeEnter (to, from, next) {
          exceptionsCompleteCheck(to, from, next)
        }
      },
      {
        path: 'PlaceException',
        name: 'WavePickingPlaceException',
        meta: { title: 'Place Exception Item' },
        component: WavePickingAppPlaceException,
        async beforeEnter (to, from, next) {
          exceptionsCompleteCheck(to, from, next)
        }
      }
    ]
  }
]
